/**
 * Per-conversation composer text, sessionStorage only.
 *
 * The chat-input store is in-memory, so a freeze/crash/reload used to wipe the
 * unsent prompt. sessionStorage survives reload (and Chromium crash-restore)
 * without writing drafts into persisted zustand/localStorage.
 */

export const COMPOSER_DRAFT_KEY_PREFIX = 'gab-composer-draft:';
export const COMPOSER_DRAFT_MAX_CHARS = 100000;
export const COMPOSER_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const SAVE_DEBOUNCE_MS = 300;

let saveTimer = null;
let pendingContextId = null;
let pendingText = null;

function flushPendingComposerDraft() {
  if (!pendingContextId) return;
  const contextId = pendingContextId;
  const text = pendingText;
  pendingContextId = null;
  pendingText = null;
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  saveComposerDraft(contextId, text);
}

function getSessionStorage() {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage;
  } catch (_) {
    return null;
  }
}

function draftKey(contextId) {
  return `${COMPOSER_DRAFT_KEY_PREFIX}${contextId}`;
}

export function sanitizeComposerDraft(raw) {
  if (typeof raw !== 'string') return '';
  const text = raw.replace(/\u0000/g, '');
  if (!text) return '';
  return text.length > COMPOSER_DRAFT_MAX_CHARS
    ? text.slice(0, COMPOSER_DRAFT_MAX_CHARS)
    : text;
}

export function saveComposerDraft(contextId, text) {
  if (!contextId) return;
  const storage = getSessionStorage();
  if (!storage) return;
  const sanitized = sanitizeComposerDraft(text);
  try {
    if (!sanitized) {
      storage.removeItem(draftKey(contextId));
      return;
    }
    storage.setItem(draftKey(contextId), JSON.stringify({ text: sanitized, ts: Date.now() }));
  } catch (_) {
    // Quota / private mode — drafts are best-effort.
  }
}

export function scheduleSaveComposerDraft(contextId, text) {
  // Native has no sessionStorage — bail before arming a per-keystroke timer.
  if (!contextId || !getSessionStorage()) return;
  pendingContextId = contextId;
  pendingText = text;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = null;
    flushPendingComposerDraft();
  }, SAVE_DEBOUNCE_MS);
}

export function readComposerDraft(contextId) {
  if (!contextId) return '';
  const storage = getSessionStorage();
  if (!storage) return '';
  try {
    const raw = storage.getItem(draftKey(contextId));
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.text !== 'string') {
      storage.removeItem(draftKey(contextId));
      return '';
    }
    if (!parsed.ts || Date.now() - parsed.ts > COMPOSER_DRAFT_MAX_AGE_MS) {
      storage.removeItem(draftKey(contextId));
      return '';
    }
    return sanitizeComposerDraft(parsed.text);
  } catch (_) {
    return '';
  }
}

export function clearComposerDraft(contextId) {
  if (!contextId) return;
  if (pendingContextId === contextId) {
    pendingContextId = null;
    pendingText = null;
    if (saveTimer) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
  }
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.removeItem(draftKey(contextId));
  } catch (_) {
    // ignore
  }
}

export function clearAllComposerDrafts() {
  pendingContextId = null;
  pendingText = null;
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    const keys = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(COMPOSER_DRAFT_KEY_PREFIX)) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  } catch (_) {
    // ignore
  }
}

// A freeze or crash never runs the debounce timer, so commit any pending draft
// as the tab goes away. `window.addEventListener` is web-only — on native
// `window` is the RN global and has no such method.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pagehide', flushPendingComposerDraft);
  window.addEventListener('visibilitychange', () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      flushPendingComposerDraft();
    }
  });
}
